import React, { createContext, useState, useContext, useMemo, ReactNode } from 'react';

export interface InfoHeaderItem {
  label: string;
  value: string | number;
  unit: string;
  icon?: string;
}

export interface TabDataItem {
  tableData?: {
    data: {
      primaryTable?: {
        name: string;
        data: Array<{ col1: string; col2: string }>;
      };
      secondaryTable?: {
        name: string;
        data: Array<{ col1: string; col2: string }>;
      };
    };
  };
  primaryChart?: {
    type: string;
    name: string;
    data: any[];
    max?: number;
    min?: number;
  };
  secondaryChart?: {
    type: string;
    name: string;
    data: any[];
  };
}

interface AnalyticsDataContextType {
  infoHeaderData: InfoHeaderItem[];
  setInfoHeaderDataExport: (data: InfoHeaderItem[]) => void;
  tabData: TabDataItem[];
  setTabDataExport: (data: TabDataItem[]) => void;
  activeTabId: string | null;
  setActiveTabId: (id: string | null) => void;
}

const AnalyticsDataContext = createContext<AnalyticsDataContextType | undefined>(undefined);

interface AnalyticsDataProviderProps {
  children: ReactNode;
}

export const AnalyticsDataProvider: React.FC<AnalyticsDataProviderProps> = ({ children }) => {
  const [infoHeaderData, setInfoHeaderData] = useState<InfoHeaderItem[]>([]);
  const [tabData, setTabData] = useState<TabDataItem[]>([]);
  const [activeTabId, setActiveTabId] = useState<string | null>(null);

  const value = useMemo(
    () => ({
      infoHeaderData,
      setInfoHeaderDataExport: setInfoHeaderData,
      tabData,
      setTabDataExport: setTabData,
      activeTabId,
      setActiveTabId
    }),
    [infoHeaderData, tabData, activeTabId]
  );

  return (
    <AnalyticsDataContext.Provider value={value}>
      {children}
    </AnalyticsDataContext.Provider>
  );
};

export const useAnalyticsData = () => {
  const context = useContext(AnalyticsDataContext);
  if (context === undefined) {
    throw new Error('useAnalyticsData must be used within an AnalyticsDataProvider');
  }
  return context;
};
